import type { InstanceConfig } from '../instance/config.ts';
import { joinUrl, parseUrl, resolveDisplayUrl } from '../instance/urls.ts';
import { formatRoute, type Route } from './routes.ts';

/**
 * §8's jump-out links: "open the thing I am looking at somewhere else".
 *
 * R2's invariant is that **no instance-origin absolute URL escapes core except via `links/`**, and
 * this is that door. Everywhere else an instance URL is either a transport URL (`resolveInstanceUrl`)
 * or nothing; here it is deliberately a URL for a human to follow out of the app.
 *
 * The implementation is one line of insight rather than a table per destination: §8's whole premise
 * is that Visious's routes *are* YouTube's, and Invidious uses the same scheme — which is what makes
 * domain-swapping work in the first place. So the outbound URL is `formatRoute(route)` joined onto
 * another origin, and the round trip is lossless by construction: `parseRoute` ignores the origin,
 * so `parseRoute(youtubeUrl(r))` is `r` again, `t` and `list` included. The testkit asserts that per
 * arm rather than trusting the symmetry to hold by inspection.
 *
 * Verified against `invidious.example.com` (2026-07-28) — every arm answers, two by redirect:
 *
 *   /watch?v=ID              200        /channel/UC…    200
 *   /playlist?list=PL…       200        /@handle        302 → /channel/UC…
 *   /hashtag/TAG             200        /results?search_query=Q  302 → /search?q=Q
 *
 * The two 302s are Invidious normalising YouTube's spelling to its own, which is exactly the
 * behaviour that makes a jump-out worth building this way. (`/c/NAME` and `/user/NAME` answer 404
 * there — they are 2f's `resolveurl` cases, and no route arm produces them.)
 */

/** YouTube's canonical web origin. `youtu.be` is a *short* link, not the canonical page. */
export const YOUTUBE_ORIGIN = 'https://www.youtube.com';

/**
 * Where a jump-out goes. `instance` rather than `invidious`: it is whichever instance §3 resolved,
 * and its name belongs to the deployment, not to this union.
 */
export type JumpOutTarget = 'youtube' | 'instance';

export interface JumpOut {
  readonly target: JumpOutTarget;
  readonly url: string;
  /**
   * The destination's host, so a caller can name the link without re-parsing the URL it was just
   * handed. Data, not a formatted string (R7) — the sentence around it is the platform's.
   */
  readonly host: string;
}

/**
 * The same route on a different origin. `null` when the route does not name a page somewhere else, or
 * when `origin` is not an absolute URL.
 *
 * Two arms are refused. `not-found` carries a path we could not read, and pointing it at YouTube would
 * only move the confusion. `login` is a page of **this app**: YouTube has no equivalent at all, the
 * instance's own `/login` is a password form rather than the grant this app uses, and a `?next=`
 * naming a Visious route means nothing on either origin.
 */
export function externalRouteUrl(origin: string, route: Route): string | null {
  if (route.kind === 'not-found' || route.kind === 'login') return null;
  return joinUrl(origin, formatRoute(route));
}

export function youtubeRouteUrl(route: Route): string | null {
  return externalRouteUrl(YOUTUBE_ORIGIN, route);
}

/**
 * The route on the configured instance's own frontend. Goes through {@link resolveDisplayUrl} rather
 * than {@link externalRouteUrl} so it is the *display* origin that is joined — R2 keeps `instanceUrl`
 * and `transportBaseUrl` separate, and a jump-out that landed on a proxy base URL would send the
 * reader to a host that only exists to answer API calls.
 */
export function instanceRouteUrl(config: InstanceConfig, route: Route): string | null {
  // The same two refusals as above, and the `login` one matters *more* here rather than less: the
  // instance really does serve `/login`, and it is its own password form — the very page this app's
  // grant flow exists to avoid sending anyone to. Refusing in only one of the two functions would also
  // make `jumpOutLinks` asymmetric, offering one destination for a route that has neither.
  if (route.kind === 'not-found' || route.kind === 'login') return null;
  return resolveDisplayUrl(config, formatRoute(route));
}

function hostOf(url: string): string | null {
  return parseUrl(url)?.host ?? null;
}

/**
 * Both destinations for one route, in the order they are offered: YouTube first because it is the
 * canonical source, the instance second because it is the one that will still be there when
 * YouTube's page is geo-blocked.
 *
 * A destination whose URL could not be built is *absent* rather than present-and-dead, which is why
 * this returns a list instead of a record with nullable fields. **`config: null` is one of those
 * cases, not an error:** §3's instance resolution is asynchronous and can fail outright, and the
 * moment a reader most wants a jump-out is when the page in front of them did not load. YouTube needs
 * no instance to link to, so it is still offered.
 */
export function jumpOutLinks(config: InstanceConfig | null, route: Route): readonly JumpOut[] {
  const out: JumpOut[] = [];
  for (const target of ['youtube', 'instance'] as const) {
    const url =
      target === 'youtube'
        ? youtubeRouteUrl(route)
        : config === null
          ? null
          : instanceRouteUrl(config, route);
    if (url === null) continue;
    const host = hostOf(url);
    if (host === null) continue;
    out.push({ target, url, host });
  }
  return out;
}
